// utils/breadcrumbGenerator.js

// Route mapping for generating paths from labels based on your actual routes
const ROUTE_MAPPING = {
    // Main sections
    'Sales': '/sales',
    'Inventory': '/inventory/products',
    'Purchase': '/purchase/rfq',
    'Accounts': '/accounts/invoices',
    'Vehicle': '/vehicle/drivers',
    'Expenses': '/Expenses',
    'Dashboard': '/dashboard',
    
    // Sales subsections
    'Customers': '/sales/customers',
    'Pricelists': '/sales/pricelist',
    'Sales Order': '/sales',
    'Quotation': '/sales/quotation',
    'Delivery Orders': '/sales/delivery-orders',
    'Return Receipt': '/sales/return-receipt',
    'Sales Return': '/sales/return',
    'Contacts': '/sales/contacts',
    
    // Inventory subsections
    'Product': '/inventory/variants',
    'Products': '/inventory/variants',
    'Variants': '/inventory/variants',
    'Product Category': '/inventory/product-categories',
    'Product Categories': '/inventory/product-categories',
    'Categories': '/inventory/product-categories',
    'Category': '/inventory/product-categories',
    'Product Attributes': '/inventory/product-attributes',
    'Attributes': '/inventory/product-attributes',
    'Attribute': '/inventory/product-attributes',
    'UOM': '/inventory/uoms',
    'UOMs': '/inventory/uoms',
    'Categories': '/inventory/uoms/categories',
    'Physical Warehouse': '/inventory/warehouses/physical',
    'Physical Warehouses': '/inventory/warehouses/physical',
    'Physical': '/inventory/warehouses/physical',
    'Mobile Warehouse': '/inventory/warehouses/mobile',
    'Mobile Warehouses': '/inventory/warehouses/mobile',
    'Mobile': '/inventory/warehouses/mobile',
    'Warehouses': '/inventory/warehouses/physical',
    
    // Purchase subsections
    'RFQ': '/purchase/rfq',
    'Purchase Return': '/purchase/return',
    'Vendors': '/purchase/vendors',
    'Goods Receipt': '/purchase/goods-receipt',
    'Goods Return': '/purchase/goods-return',
    'Batch tracking': '/purchase/batch-tracking',
    'Batch Tracking': '/purchase/batch-tracking',
    
    // Vehicle subsections
    'Maintenance': '/vehicle/maintenance',
    'Penalties': '/vehicle/penalties',
    'Drivers': '/vehicle/drivers',
    'Type': '/vehicle/type',
    
    // Accounts subsections
    'Invoices': '/accounts/invoices',
    'Credit Note': '/accounts/credit-notes',
    'Chart of Account': '/accounts/configuration/chart-of-account',
    'Currency': '/accounts/configuration/currency',
    'Journals': '/accounts/configuration/journals',
    'Taxes': '/accounts/configuration/taxes',
    'Customer Payment': '/accounts/customer-payments',
    'Journal Entries': '/accounts/journal-entries',
    'Refunds': '/accounts/refunds',
    'Vendor Bills': '/accounts/vendor-bills',
    'Vendor Payments': '/accounts/vendor-payments',
    
    // User Management
    'My Profile': '/my-profile',
    'My Organization': '/my-organization',
    'User Accounts': '/user-accounts',
    'User Roles': '/user-roles',
    'Roles': '/user-roles',
    'Role': '/user-roles',
    'Users': '/user-accounts',
    
    // Actions (these typically don't have their own routes, they're part of the parent)
    'Create': '',
    'Add': '',
    'Edit': '',
    'Details': '',
    'View': '',
    'List': ''
  };
  
  // Function to convert pageName and nestedpageName to breadcrumbs array
export const generateBreadcrumbsFromPageName = (pageName = '', nestedpageName = '', currentPath = '') => {
    const breadcrumbs = [];
    
    // Clean and split pageName
    // const cleanPageName = pageName.replace(/>/g, '').trim();
    // const pageSegments = cleanPageName.split(/[\/\>]/).map(segment => segment.trim()).filter(segment => segment);
    const pageSegments = pageName.split(/[\/\>]/).map(segment => segment.trim()).filter(segment => segment);
    
    // Check if we have a meaningful nested page (not just an action)
    const hasActionPage = nestedpageName && ['Create', 'Edit', 'Details', 'View', 'List'].includes(nestedpageName);
    const hasRealNestedPage = nestedpageName && !hasActionPage;
    
    // Build breadcrumbs for page segments
    let accumulatedSegments = [];
    
    pageSegments.forEach((segment, index) => {
      if (segment) {
        accumulatedSegments.push(segment);
        const path = generatePathFromSegments(accumulatedSegments);
        const isLastSegment = index === pageSegments.length - 1;
        
        // Determine if this segment should be clickable
        let shouldBeClickable = false;
        
        if (hasRealNestedPage) {
          // If we have a real nested page, only intermediate segments can be clickable
          // Last segment is not clickable because we're in its sub-section
          shouldBeClickable = !isLastSegment && path !== currentPath;
        } else if (hasActionPage) {
          // If we have an action page, the last meaningful segment should be clickable
          // unless we're currently on that page
          shouldBeClickable = isLastSegment && path !== currentPath;
        } else {
          // No nested page, last segment is current page, not clickable
          shouldBeClickable = false;
        }
        
        breadcrumbs.push({
          label: segment,
          path: shouldBeClickable ? path : undefined
        });
      }
    });
    
    // Add nested page name as breadcrumb (always not clickable - it's current page)
    if (nestedpageName) {
      breadcrumbs.push({
        label: nestedpageName,
        path: undefined // Current page is never clickable
      });
    }
    
    return breadcrumbs;
  };
  
  // Generate path from accumulated segments
  const generatePathFromSegments = (segments) => {
    if (segments.length === 0) return '/dashboard';
    
    // For single segment, use direct mapping
    if (segments.length === 1) {
      const mapped = ROUTE_MAPPING[segments[0]];
      if (mapped) return mapped;
      // Fallback to lowercase with dashes
      return `/${segments[0].toLowerCase().replace(/\s+/g, '-')}`;
    }
    
    // For multiple segments, try to build hierarchical path
    const firstSegment = segments[0];
    const lastSegment = segments[segments.length - 1];
    
    // Check if we have a direct mapping for the last segment
    const lastSegmentMapped = ROUTE_MAPPING[lastSegment];
    if (lastSegmentMapped && lastSegmentMapped !== '') {
      return lastSegmentMapped;
    }
    
    // Check if we have a base path for the first segment
    const basePath = ROUTE_MAPPING[firstSegment];
    if (!basePath) {
      return `/${segments.join('/').toLowerCase().replace(/\s+/g, '-')}`;
    }
    
    // Build path based on known patterns in your routes
    if (segments.length === 2) {
      // Try common patterns
      const secondSegment = segments[1];
      const possiblePaths = [
        `${basePath}/${secondSegment.toLowerCase().replace(/\s+/g, '-')}`,
        `${basePath}/${secondSegment.toLowerCase()}`,
        basePath // Sometimes the second segment might not add to path
      ];
      
      // Check against known routes (you could expand this)
      for (const path of possiblePaths) {
        if (Object.values(ROUTE_MAPPING).includes(path)) {
          return path;
        }
      }
      
      // Default to first possibility
      return possiblePaths[0];
    }
    
    // For more complex cases, build incrementally
    let currentPath = basePath;
    for (let i = 1; i < segments.length; i++) {
      const segment = segments[i];
      if (ROUTE_MAPPING[segment] === '') continue; // Skip action words
      
      currentPath += `/${segment.toLowerCase().replace(/\s+/g, '-')}`;
    }
    
    return currentPath;
  };